import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { createFileRoute } from '@tanstack/react-router'
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { DragEndEvent } from '@dnd-kit/core'

import type {
    ShopCategory,
    ShopCategoryInput,
} from '@/lib/api/shop-catalog-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    setShopServerErrors,
    shopFieldErrors,
} from '@/components/retail/shop-errors'
import {
    ErrorText,
    SwitchField,
    TextField,
} from '@/components/retail/form-bits'
import { ShopImageField } from '@/components/retail/shop-image-field'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FieldError } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useDeleteShopCategory,
    useReorderShopCategories,
    useSaveShopCategory,
    useShopCategories,
} from '@/hooks/use-shop-catalog-query'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Danh mục bán (dùng chung web + Zalo Mini App). Thứ tự = vị trí kéo
// thả; thả là lưu, có "Hoàn tác" 5 giây.
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/retail/categories',
)({
    component: CategoriesPage,
})

// Giá trị form: ô chữ luôn là chuỗi (trống = null khi gửi).
interface CategoryForm {
    name: string
    slug: string
    image_url: string | null
    description: string
    show_web: boolean
    show_miniapp: boolean
    seo_title: string
    seo_description: string
}

const EMPTY: CategoryForm = {
    name: '',
    slug: '',
    image_url: null,
    description: '',
    show_web: true,
    show_miniapp: true,
    seo_title: '',
    seo_description: '',
}

// Server vẫn kiểm đủ; ở đây chỉ chặn tên trống để báo ngay cạnh ô.
const categorySchema = () =>
    z.custom<CategoryForm>().superRefine((f, ctx) => {
        if (!f.name.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['name'],
                message: m.shop_category_name_required(),
            })
        }
    })

export function toCategoryInput(c: ShopCategory): ShopCategoryInput {
    return {
        name: c.name,
        slug: c.slug,
        image_url: c.image_url,
        description: c.description,
        show_web: c.show_web,
        show_miniapp: c.show_miniapp,
        seo_title: c.seo_title,
        seo_description: c.seo_description,
    }
}

const nullable = (v: string) => (v.trim() === '' ? null : v)

function toForm(c: ShopCategory): CategoryForm {
    return {
        ...toCategoryInput(c),
        description: c.description ?? '',
        seo_title: c.seo_title ?? '',
        seo_description: c.seo_description ?? '',
    }
}

function fromForm(f: CategoryForm): ShopCategoryInput {
    return {
        ...f,
        description: nullable(f.description),
        seo_title: nullable(f.seo_title),
        seo_description: nullable(f.seo_description),
    }
}

export function CategoriesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_catalog.write')
    const query = useShopCategories()
    const save = useSaveShopCategory()
    const remove = useDeleteShopCategory()
    const reorder = useReorderShopCategories()
    const [order, setOrder] = useState<string[] | null>(null)
    const [editing, setEditing] = useState<ShopCategory | 'new' | null>(null)
    const form = useForm<CategoryForm>({
        resolver: zodResolver(categorySchema()),
        defaultValues: EMPTY,
    })

    const server = query.data?.data ?? []
    const byId = new Map(server.map((c) => [c.id, c]))
    const rows = order
        ? order.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []))
        : server

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )

    const persistOrder = (next: string[], previous: string[]) => {
        setOrder(next)
        reorder.mutate(next, {
            onSuccess: () => {
                setOrder(null)
                toast.success(m.shop_category_reordered(), {
                    duration: 5000,
                    action: {
                        label: m.shop_undo(),
                        onClick: () =>
                            reorder.mutate(previous, {
                                onSettled: () => setOrder(null),
                            }),
                    },
                })
            },
            onError: () => setOrder(null),
        })
    }

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return
        const ids = rows.map((r) => r.id)
        const from = ids.indexOf(String(e.active.id))
        const to = ids.indexOf(String(e.over.id))
        persistOrder(arrayMove(ids, from, to), ids)
    }

    const open = (c: ShopCategory | 'new') => {
        setEditing(c)
        form.reset(c === 'new' ? EMPTY : toForm(c))
    }

    const submit = form.handleSubmit(async (values) => {
        if (editing === null) return
        try {
            const res = await save.mutateAsync({
                id: editing === 'new' ? undefined : editing.id,
                input: fromForm(values),
            })
            if (res.data) {
                setEditing(res.data)
                form.reset(toForm(res.data))
            }
        } catch (err) {
            setShopServerErrors(
                form.setError,
                err,
                m.shop_category_save_failed(),
            )
        }
    })

    const toggle = (c: ShopCategory, key: 'show_web' | 'show_miniapp') => {
        save.mutate(
            { id: c.id, input: { ...toCategoryInput(c), [key]: !c[key] } },
            {
                onError: (err) =>
                    toast.error(
                        shopFieldErrors(err, m.shop_category_save_failed())
                            .form ?? m.shop_category_save_failed(),
                    ),
            },
        )
    }

    const colCount = canWrite ? 8 : 6

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_category_title()}
                subtitle={
                    canWrite
                        ? m.shop_category_subtitle()
                        : m.shop_category_subtitle_readonly()
                }
                primaryActions={
                    canWrite ? (
                        <Button size="sm" onClick={() => open('new')}>
                            <Plus className="mr-1 size-4" />
                            {m.shop_category_add()}
                        </Button>
                    ) : undefined
                }
            />
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
                <div className="overflow-x-auto rounded-md border">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDragEnd}
                    >
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {canWrite && <TableHead className="w-8" />}
                                    <TableHead className="w-10">#</TableHead>
                                    <TableHead>
                                        {m.shop_category_name_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.shop_category_path_label()}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {m.shop_category_group_count()}
                                    </TableHead>
                                    <TableHead className="text-center">
                                        {m.shop_show_web()}
                                    </TableHead>
                                    <TableHead className="text-center">
                                        {m.shop_show_miniapp()}
                                    </TableHead>
                                    {canWrite && <TableHead className="w-16" />}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {query.isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={colCount}
                                            className="text-muted-foreground"
                                        >
                                            {m.common_loading()}
                                        </TableCell>
                                    </TableRow>
                                ) : query.isError ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={colCount}
                                            className="text-destructive"
                                        >
                                            {m.shop_category_load_failed()}
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={colCount}
                                            className="py-8 text-center text-muted-foreground"
                                        >
                                            {m.shop_category_empty()}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <SortableContext
                                        items={rows.map((r) => r.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {rows.map((c, i) => (
                                            <CategoryRow
                                                key={c.id}
                                                category={c}
                                                index={i}
                                                canWrite={canWrite}
                                                selected={
                                                    editing !== 'new' &&
                                                    editing?.id === c.id
                                                }
                                                onEdit={() => open(c)}
                                                onToggle={(k) => toggle(c, k)}
                                            />
                                        ))}
                                    </SortableContext>
                                )}
                            </TableBody>
                        </Table>
                    </DndContext>
                </div>

                {canWrite && (
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle className="text-base">
                                {editing === null
                                    ? m.shop_category_form_hint()
                                    : editing === 'new'
                                      ? m.shop_category_add()
                                      : m.shop_category_edit_title({
                                            name: editing.name,
                                        })}
                            </CardTitle>
                        </CardHeader>
                        {editing !== null && (
                            <CardContent>
                                <FormProvider {...form}>
                                    <form
                                        onSubmit={submit}
                                        className="space-y-4"
                                        noValidate
                                    >
                                        <TextField
                                            name="name"
                                            label={`${m.shop_category_name_label()} *`}
                                        />
                                        <TextField
                                            name="slug"
                                            label={m.shop_slug_label()}
                                            hint={m.shop_slug_hint()}
                                        />
                                        <div className="space-y-1.5">
                                            <Label htmlFor="cat-image">
                                                {m.shop_category_image_label()}
                                            </Label>
                                            <Controller
                                                control={form.control}
                                                name="image_url"
                                                render={({ field }) => (
                                                    <ShopImageField
                                                        id="cat-image"
                                                        label={m.shop_category_image_label()}
                                                        value={field.value}
                                                        onChange={
                                                            field.onChange
                                                        }
                                                        className="max-w-24"
                                                    />
                                                )}
                                            />
                                            <FieldError name="image_url" />
                                        </div>
                                        <TextField
                                            name="description"
                                            label={m.shop_category_description_label()}
                                            rows={2}
                                        />
                                        <SwitchField
                                            name="show_web"
                                            label={m.shop_show_on_web()}
                                        />
                                        <SwitchField
                                            name="show_miniapp"
                                            label={m.shop_show_on_miniapp()}
                                        />
                                        <details className="rounded-md border p-3">
                                            <summary className="cursor-pointer text-sm font-medium">
                                                {m.shop_seo_section()}
                                            </summary>
                                            <div className="mt-3 space-y-3">
                                                <TextField
                                                    name="seo_title"
                                                    label={m.shop_seo_title()}
                                                />
                                                <TextField
                                                    name="seo_description"
                                                    label={m.shop_seo_description()}
                                                />
                                            </div>
                                        </details>
                                        <p className="text-xs text-muted-foreground">
                                            {m.shop_category_order_note()}
                                        </p>
                                        <ErrorText
                                            text={
                                                form.formState.errors.root
                                                    ?.server?.message
                                            }
                                        />
                                        <div className="flex justify-between gap-2">
                                            {editing !== 'new' ? (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    className="text-destructive"
                                                    disabled={remove.isPending}
                                                    onClick={() => {
                                                        if (
                                                            !window.confirm(
                                                                m.shop_category_delete_confirm(
                                                                    {
                                                                        name: editing.name,
                                                                    },
                                                                ),
                                                            )
                                                        )
                                                            return
                                                        remove.mutate(
                                                            editing.id,
                                                            {
                                                                onSuccess: () =>
                                                                    setEditing(
                                                                        null,
                                                                    ),
                                                            },
                                                        )
                                                    }}
                                                >
                                                    <Trash2 className="mr-1 size-4" />
                                                    {m.common_delete()}
                                                </Button>
                                            ) : (
                                                <span />
                                            )}
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setEditing(null)
                                                    }
                                                >
                                                    {m.common_cancel()}
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    disabled={save.isPending}
                                                >
                                                    {save.isPending
                                                        ? m.common_saving()
                                                        : m.common_save()}
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </FormProvider>
                            </CardContent>
                        )}
                    </Card>
                )}
            </div>
        </div>
    )
}

function CategoryRow({
    category: c,
    index,
    canWrite,
    selected,
    onEdit,
    onToggle,
}: {
    category: ShopCategory
    index: number
    canWrite: boolean
    selected: boolean
    onEdit: () => void
    onToggle: (key: 'show_web' | 'show_miniapp') => void
}) {
    const sortable = useSortable({ id: c.id, disabled: !canWrite })
    const style = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
    }
    return (
        <TableRow
            ref={sortable.setNodeRef}
            style={style}
            data-state={selected ? 'selected' : undefined}
            className={sortable.isDragging ? 'relative z-10 bg-muted' : ''}
        >
            {canWrite && (
                <TableCell>
                    <button
                        type="button"
                        className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2"
                        aria-label={m.shop_drag_aria({ name: c.name })}
                        {...sortable.attributes}
                        {...sortable.listeners}
                    >
                        <GripVertical className="size-4" />
                    </button>
                </TableCell>
            )}
            <TableCell className="tabular-nums text-muted-foreground">
                {index + 1}
            </TableCell>
            <TableCell className="font-medium">{c.name}</TableCell>
            <TableCell className="text-muted-foreground">
                /danh-muc/{c.slug}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {c.group_count}
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={c.show_web}
                    disabled={!canWrite}
                    aria-label={m.shop_toggle_web_aria({ name: c.name })}
                    onCheckedChange={() => onToggle('show_web')}
                />
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={c.show_miniapp}
                    disabled={!canWrite}
                    aria-label={m.shop_toggle_miniapp_aria({ name: c.name })}
                    onCheckedChange={() => onToggle('show_miniapp')}
                />
            </TableCell>
            {canWrite && (
                <TableCell>
                    <Button size="sm" variant="ghost" onClick={onEdit}>
                        {m.common_edit()}
                    </Button>
                </TableCell>
            )}
        </TableRow>
    )
}
